/**
 * Survey calendar: Julian (day-of-year) days as used in project folders (`Day_283`), ISO weeks for the
 * weekly client report (Monday to Sunday, always 7 days; MB 2026-10-16), and TJR's `dd/mm/yyyy` dates.
 */

const MS_PER_DAY = 86_400_000;

/** Day of year (1–366) for a UTC date. */
export function dateToJday(date: Date): number {
  const start = Date.UTC(date.getUTCFullYear(), 0, 1);
  return Math.floor((date.getTime() - start) / MS_PER_DAY) + 1;
}

/** UTC date for a day of year in `year`. */
export function jdayToDate(year: number, jday: number): Date {
  return new Date(Date.UTC(year, 0, jday));
}

export interface IsoWeek {
  readonly isoYear: number;
  readonly week: number;
}

/** ISO 8601 week (Monday-based, week 1 contains 4 January). */
export function isoWeekOf(date: Date): IsoWeek {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNr = (d.getUTCDay() + 6) % 7; // Monday = 0
  d.setUTCDate(d.getUTCDate() - dayNr + 3); // Thursday of this week decides the year
  const isoYear = d.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(isoYear, 0, 4));
  const firstThursdayNr = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstThursdayNr + 3);
  const week = 1 + Math.round((d.getTime() - firstThursday.getTime()) / (7 * MS_PER_DAY));
  return { isoYear, week };
}

/** ISO week number of a Julian day in `year` (the report's "Week 41"). */
export function isoWeek(year: number, jday: number): number {
  return isoWeekOf(jdayToDate(year, jday)).week;
}

/** Monday of an ISO week as a UTC date. */
export function isoWeekMonday(isoYear: number, week: number): Date {
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const jan4Nr = (jan4.getUTCDay() + 6) % 7;
  const monday = new Date(jan4.getTime() - jan4Nr * MS_PER_DAY);
  monday.setUTCDate(monday.getUTCDate() + (week - 1) * 7);
  return monday;
}

export interface WeekDays {
  /** Julian day of the Monday. */
  readonly d0: number;
  /** Julian day of the Sunday, in the Sunday's own year: d0 + 6, or 1 to 3 for a week 53 that ends in January. */
  readonly d1: number;
}

/**
 * First and last Julian day of an ISO week (strictly Monday to Sunday), for labels such as "Day 278 to Day 284".
 * Never compare day numbers to decide what is in a week: week 53 of 2026 runs from day 362 to day 3 of 2027, so a
 * day-number range is wrong across New Year. Use `weekDates` and `inWeek` on the flight's date instead.
 */
export function weekDays(year: number, week: number): WeekDays {
  const w = weekDates(year, week);
  return { d0: dateToJday(isoDateToDate(w.monday)), d1: dateToJday(isoDateToDate(w.sunday)) };
}

/** An ISO week as full dates (`YYYY-MM-DD`), so that New Year makes no difference. */
export interface WeekDates {
  readonly monday: string;
  readonly sunday: string;
}

/** The Monday and Sunday of an ISO week as `YYYY-MM-DD`. */
export function weekDates(isoYear: number, week: number): WeekDates {
  const monday = isoWeekMonday(isoYear, week);
  return { monday: isoDate(monday), sunday: isoDate(addUtcDays(monday, 6)) };
}

/** The day numbers of an ISO week's seven days, Monday first: `[362, 363, 364, 365, 1, 2, 3]` for week 53 of 2026. */
export function weekDayNumbers(isoYear: number, week: number): number[] {
  const monday = isoWeekMonday(isoYear, week);
  return Array.from({ length: 7 }, (_, i) => dateToJday(addUtcDays(monday, i)));
}

/** Whether a `YYYY-MM-DD` date falls in the week (Monday to Sunday inclusive). */
export function inWeek(date: string, w: WeekDates): boolean {
  return date >= w.monday && date <= w.sunday;
}

/** `YYYY-MM-DD` of a UTC date. */
export function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** A `YYYY-MM-DD` date as a UTC midnight `Date`. */
export function isoDateToDate(date: string): Date {
  return new Date(`${date}T00:00:00Z`);
}

/** A `YYYY-MM-DD` date moved by `days` (negative for earlier). */
export function addIsoDays(date: string, days: number): string {
  return isoDate(addUtcDays(isoDateToDate(date), days));
}

function addUtcDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * MS_PER_DAY);
}

/**
 * Today's calendar date where the person is (the laptop's or browser's local zone, as the daily entry and the
 * quote date it), as a UTC midnight `Date` for `dateToJday` and `isoWeekOf`. The UTC date is wrong until 08:00 in
 * Western Australia: it still shows yesterday's day number, and day 365 / week 53 on the morning of 1 January.
 */
export function localCalendarDate(now: Date = new Date()): Date {
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
}

/** Today's `YYYY-MM-DD` in the local zone (see `localCalendarDate`). */
export function localIsoDate(now: Date = new Date()): string {
  return isoDate(localCalendarDate(now));
}

/** Report tag, e.g. `WK41`: the figure file names (`WK41_Milgun_Week.png`) and the week of a label. */
export function weekTag(week: number): string {
  return `WK${String(week).padStart(2, '0')}`;
}

/**
 * The weekly report's period, `2026-WK41` (decision 487): with its ISO year, so a second year's week 41 is a new
 * report and never a revision of the first year's. Week 53 of 2026 (28 Dec 2026 to 3 Jan 2027) is `2026-WK53`.
 */
export function weeklyPeriod(isoYear: number, week: number): string {
  return `${isoYear}-${weekTag(week)}`;
}

/** `2026-WK41` → { year: 2026, week: 41 }; `WK41` (a report from before decision 487) → { year: null, week: 41 }. */
export function parseWeeklyPeriod(
  period: string,
): { readonly year: number | null; readonly week: number } | null {
  const m = /^(?:(\d{4})-)?WK(\d{2})$/.exec(period.trim());
  if (!m) return null;
  return { year: m[1] ? Number(m[1]) : null, week: Number(m[2]) };
}

/** The week's figure folder under the figures folder, `2026_WK41` (decision 487); `WK41` was the name before. */
export function weeklyFiguresFolder(isoYear: number, week: number): string {
  return `${isoYear}_${weekTag(week)}`;
}

/** Parse TJR's `dd/mm/yyyy` into a UTC date. */
export function parseTjrDate(text: string): Date {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text.trim());
  if (!m) throw new RangeError(`invalid date "${text}" (expected dd/mm/yyyy)`);
  return new Date(Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1])));
}

/** Format a UTC date as TJR's `dd/mm/yyyy`. */
export function formatTjrDate(date: Date): string {
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${date.getUTCFullYear()}`;
}
